import { BlockPrimitive, DataChangeStruct, PrimitiveType, PythaEngineControl, PythaEvent, PythaEventBlock, PythaEventName, UpdateType } from 'pytha2';
import Draw from '../draw/draw';
import './index.less';
import { useLocation, useParams } from 'react-router-dom';
import { useContext, useEffect, useRef, useState } from 'react';
import { EngineContext, NavigationContext } from '@/App';
import { findBlock, updateBlock } from '@/declare/api/geometry/block';
import { message } from 'antd';
import { Matrix4, Vector3 } from 'three';
import { BaseUtils } from 'tnbase';
import { emitter } from '@/utils/mitt';
import { Emit_Event_Common_Tool } from '@/declare/enums/emit-event';


export default function BlockDetailPanel() {

    const location = useLocation();
    const { projectUuid, blockUuid } = useParams();
    const engineContext = useContext(EngineContext);
    const navigateContext = useContext(NavigationContext);
    const blockUuidRef = useRef<string>(null);
    const initRef = useRef(false);  // 是否初始化完成
    const [initUuid, setInitUuid] = useState<string>(null);  // 初始化的uuid
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const [pickPointEventUuid, setPickPointEventUuid] = useState<string>(null);
    const isComponentMountedRef = useRef(false);

    useEffect(() => {
        isComponentMountedRef.current = true; // 组件挂载

        return () => {
            isComponentMountedRef.current = false; // 组件卸载
        };
    }, []);

    useEffect(() => {
        blockUuidRef.current = blockUuid;
    }, [blockUuid])

    useEffect(() => {
        if (blockUuid == null || engineContext.engine == null || engineContext.engine.storage == null) return;

        if (timeoutIdRef.current != null) {
            clearTimeout(timeoutIdRef.current);
        }

        // draw里会有两次switchOn，要等第二次初始化完才是后续使用的引擎
        timeoutIdRef.current = setTimeout(() => {
            setInitUuid(BaseUtils.genUUID());
        }, 100)

        return () => {
            if (timeoutIdRef.current != null) {
                clearTimeout(timeoutIdRef.current);
            }
        }
    }, [engineContext.engine, blockUuid])

    useEffect(() => {
        if (initUuid == null) return;
        // 使用副作用才能保证拿到最新的上下文，获取到最新的引擎
        generateData();
    }, [initUuid])

    useEffect(() => {
        emitter.on(Emit_Event_Common_Tool.PICK_POINT, onListenPickPoint);

        return () => {
            emitter.off(Emit_Event_Common_Tool.PICK_POINT, onListenPickPoint);
        }
    }, [])

    useEffect(() => {
        if (pickPointEventUuid == null) return;
        engineContext.emitCommand("PICK_POINT");
    }, [pickPointEventUuid])

    const onListenPickPoint = () => {
        setPickPointEventUuid(BaseUtils.genUUID());
    }

    // 获取块模板数据
    const generateData = () => {
        if (!isComponentMountedRef.current) return;
        findBlock(blockUuid).then(res => {
            if (!isComponentMountedRef.current) return;
            const block = res.data;
            navigateContext.registTab({ key: location.pathname, value: block.properties.name })
            navigateContext.setActiveTabKey(location.pathname);
            // 清空图元
            Object.keys(engineContext?.engine?.storage?.primitiveStore?.primitiveDict || {})?.forEach(uuid => {
                const primitive = engineContext?.engine?.storage?.primitiveStore?.primitiveDict[uuid];
                primitive.needDelete = true;
            });
            engineContext?.engine?.eventMediator?.renderHandler?.render(true, UpdateType.ALL);

            // 不需要加入到场景里，只需要加入到blockStore就行
            engineContext?.engine.registBlocks([block.properties]);
            setTimeout(() => {
                if (!isComponentMountedRef.current) return;
                // engineContext.engine.resolvePrimitives([block.properties]);
                // 发信号，炸开块
                engineContext.emitCommand("EXPLODE_BLOCK");
                let event = new PythaEventBlock();
                event.data = { "blockTemplateUuid": block.uuid, "needNotice": false };
                engineContext?.engine?.eventMediator?.emitEvent(event);
                setTimeout(() => {
                    if (!isComponentMountedRef.current) return;
                    initRef.current = true;
                }, 100)
            }, 0)
        }).catch(err => {
            console.log('err', err)
            message.error("获取块模板数据失败");
        })
    }

    // 监听引擎事件
    const onSubscribe = (event: PythaEvent, engine: PythaEngineControl) => {
        if (event.cmd === PythaEventName.DATA_CHANGE) {
            if (!initRef.current) return;
            let data = event?.data as DataChangeStruct;
            if (!data) return;
            if ((data.add_primitives && data.add_primitives.length > 0) || (data.delete_primitives && data.delete_primitives.length > 0)
                || data.delete_block_template != null || data.add_block_template != null) {
                setTimeout(() => {
                    finish(engine);
                }, 100)
            } else if (data.point) {
                onPickPoint(data.point.x, data.point.y, data.point.z, engine);
            }
            return false;
        }
        return true;
    }

    // 自动完成块的组合
    const finish = (engine) => {
        if (blockUuidRef.current == null || engine == null) return;
        const bt = engine.storage.blockStore.blockTemplateDict[blockUuidRef.current] as BlockPrimitive;
        if (bt == null) return;
        // 尝试组合新的块模板
        const primitiveDict = engine.storage.primitiveStore.primitiveDict;
        bt.setChildren([]);
        Object.keys(primitiveDict).forEach(uuid => {
            const primitive = primitiveDict[uuid];
            if (primitive.type != PrimitiveType.Block) {
                const clone = primitive.clone();
                clone.layerUuid = null;
                if (clone.localModelMatrix == null) {
                    clone.localModelMatrix = new Matrix4();
                }
                clone.localModelMatrix.premultiply(bt.worldModelMatrixInverse);
                bt.addChildPrimitive(clone);
            } else {
                //新增新的块模板图元
                let child = new BlockPrimitive();
                child.localModelMatrix.copy(primitive.localModelMatrix);

                child.blockTemplateUuid = (primitive as BlockPrimitive).blockTemplateUuid;
                child.localModelMatrix.premultiply(bt.worldModelMatrixInverse);
                bt.addChildPrimitive(child);
            }
        })
        bt.updateWorldModelMatrix(false, true);

        updateBlock(blockUuidRef.current, bt.toJson()).then().catch(err => {
            message.error("编辑块失败");
        })
    }

    // 处理点击基点
    const onPickPoint = (x: number, y: number, z: number, engine) => {
        if (blockUuidRef.current == null || engine == null) return;
        const bt = engine.storage.blockStore.blockTemplateDict[blockUuidRef.current] as BlockPrimitive;
        if (bt == null) return;
        const local = bt.localModelMatrix;
        local.elements[12] = x;
        local.elements[13] = y;
        local.elements[14] = z;
        base.set(x, y, z);
        axisX.set(local.elements[0], local.elements[1], local.elements[2]);
        axisY.set(local.elements[4], local.elements[5], local.elements[6]);
        axisZ.set(local.elements[8], local.elements[9], local.elements[10]);
        bt.setXYZP(axisX, axisY, axisZ, base);
        // 尝试组合新的块模板
        const primitiveDict = engine.storage.primitiveStore.primitiveDict;
        bt.setChildren([]);
        Object.keys(primitiveDict).forEach(uuid => {
            const primitive = primitiveDict[uuid];
            if (primitive.type != PrimitiveType.Block) {
                const clone = primitive.clone();
                clone.layerUuid = null;
                if (clone.localModelMatrix == null) {
                    clone.localModelMatrix = new Matrix4();
                }
                clone.localModelMatrix.premultiply(bt.worldModelMatrixInverse);
                bt.addChildPrimitive(clone);
            } else {
                //新增新的块模板图元
                let child = new BlockPrimitive();
                child.localModelMatrix.copy(primitive.localModelMatrix);

                child.blockTemplateUuid = (primitive as BlockPrimitive).blockTemplateUuid;
                child.localModelMatrix.premultiply(bt.worldModelMatrixInverse);
                bt.addChildPrimitive(child);
            }
        })
        bt.updateWorldModelMatrix(false, true);
        // Object.keys(bt.children).forEach(key=>{
        //     const child = bt.children[key] as BlockPrimitive;
        // })

        updateBlock(blockUuidRef.current, bt.toJson()).then().catch(err => {
            message.error("修改块基点失败");
        })
    }

    return (
        <div className="block-detail-panel">
            <Draw onSubscribe={onSubscribe} />
        </div>
    )
}

const axisX = new Vector3();
const axisY = new Vector3();
const axisZ = new Vector3();
const base = new Vector3();